import type {
  ICardPaymentBrickPayer,
  ICardPaymentFormData,
} from '@mercadopago/sdk-react/bricks/cardPayment/type'
import { useRollbar } from '@rollbar/react'
import { PaymentMethodEnum } from 'common/enums/PaymentMethodEnum'
import { BadRequest, InternalError, NetworkError } from 'common/errors'
import { buyUpsellOffer } from 'publisher/api/mercadoPagoApi'
import { ThreeDsInfo } from 'publisher/paymentProcessors/mercadoPago/components/ThreeDsModal'
import { UpsellPaymentPayloadInterface } from 'publisher/paymentProcessors/mercadoPago/types/UpsellPaymentPayloadInterface'
import { usePayment } from 'publisher/store'
import { getActivePaymentMethod } from 'publisher/store/payment/paymentSelectors'
import { useCallback, useRef } from 'react'
import { UpsellButtonEntityInterface } from 'src/shared/entities/upsell-button/upsell-button.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import useUpsellSubmit from './useUpsellSubmit'

interface UseMercadoPagoUpsellSubmitParams {
  entity: UpsellButtonEntityInterface
  showCardFormModal: () => void
  closeModalAndUnmount: () => Promise<void>
  onThreeDsOpen: (threeDsInfo: ThreeDsInfo, redirect?: string) => void
}

export const useMercadoPagoUpsellSubmit = ({
  entity,
  showCardFormModal,
  closeModalAndUnmount,
  onThreeDsOpen,
}: UseMercadoPagoUpsellSubmitParams) => {
  const { t } = useTypedTranslation(['publisher'])
  const rollbar = useRollbar()
  const paymentMethod = usePayment(getActivePaymentMethod)

  const { errors, setErrors, isLoading, submit } = useUpsellSubmit(entity)
  const submitRef = useRef(submit)
  submitRef.current = submit

  const depsRef = useRef({
    t,
    rollbar,
    setErrors,
    closeModalAndUnmount,
    onThreeDsOpen,
  })
  depsRef.current = {
    t,
    rollbar,
    setErrors,
    closeModalAndUnmount,
    onThreeDsOpen,
  }

  const isMercadoPagoPixOrBoleto =
    paymentMethod === PaymentMethodEnum.MercadoPagoPix ||
    paymentMethod === PaymentMethodEnum.MercadoPagoBrPix ||
    paymentMethod === PaymentMethodEnum.MercadoPagoBoleto ||
    paymentMethod === PaymentMethodEnum.MercadoPagoBrBoleto

  const handleSubmitPixOrBoleto = async () => {
    await submitRef.current(
      async (
        body: UpsellPaymentPayloadInterface['upsell_payment_form'],
        pageId: number,
        purchaseProcessId: string,
      ) => {
        try {
          const { data } = await buyUpsellOffer(pageId, purchaseProcessId, {
            upsell_payment_form: body,
          })

          if (data.redirect) {
            window.location.assign(data.redirect)
          }
        } catch (error) {
          const { t, rollbar, setErrors } = depsRef.current
          if (error instanceof BadRequest) {
            setErrors(error.response.data.errors.common)
          } else if (error instanceof NetworkError) {
            setErrors([t('core.errors.no_connection')])
          } else if (error instanceof InternalError) {
            setErrors([t('core.error.title')])
          } else {
            rollbar.error('mercado pago upsell failed', error as Error)
            setErrors([t('core.error.title')])
          }
        }
      },
    )
  }

  const processPayment = useCallback(
    async (cardFormData: ICardPaymentFormData<ICardPaymentBrickPayer>) => {
      const { closeModalAndUnmount, onThreeDsOpen } = depsRef.current

      await submitRef.current(
        async (body: any, pageId: number, purchaseProcessId: string) => {
          try {
            const { data } = await buyUpsellOffer(pageId, purchaseProcessId, {
              upsell_payment_form: {
                ...body,
                token: cardFormData.token,
                mercadoPagoPaymentMethod: cardFormData.payment_method_id,
                installments: cardFormData.installments,
              },
            })

            if (data.threeDsInfo) {
              onThreeDsOpen(data.threeDsInfo, data.redirect)
              await closeModalAndUnmount()
              return
            }

            await closeModalAndUnmount()
            window.location.assign(data.redirect)
          } catch (error) {
            const { t, rollbar, setErrors } = depsRef.current
            if (error instanceof BadRequest) {
              setErrors(error.response.data.errors.common)
            } else if (error instanceof NetworkError) {
              setErrors([t('core.errors.no_connection')])
            } else if (error instanceof InternalError) {
              setErrors([t('core.error.title')])
            } else {
              rollbar.error('mercado pago upsell failed', error as Error)
              setErrors([t('core.error.title')])
            }
          }
        },
      )
    },
    [],
  )

  const handleSubmit = async () => {
    if (isMercadoPagoPixOrBoleto) {
      await handleSubmitPixOrBoleto()
      return
    }

    showCardFormModal()
  }

  return {
    handleSubmit,
    processPayment,
    isLoading,
    errors,
    setErrors,
  }
}
